import React from 'react';
import { useTranslation } from 'react-i18next';

import arrowIcon from '@/assets/icons/arrowIcon.svg';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

export interface Suggestion {
  description?: string;
  icon?: 'checklist' | 'clock' | React.ReactNode;
  id: string;
  title: string;
  /** i18n key for title; when set, title is used as fallback */
  titleKey?: string;
}

interface SuggestionCardsProps {
  onSelect?: (suggestion: Suggestion) => void;
  suggestions: Array<Suggestion>;
}

export function SuggestionCards({ onSelect, suggestions }: SuggestionCardsProps) {
  const { t } = useTranslation();
  if (!suggestions.length) {
    return null;
  }

  return (
    <div className="flex w-full flex-col gap-0">
      {suggestions.map((suggestion, index) => (
        <React.Fragment key={suggestion.id}>
          {index > 0 && <div className="bg-elevated-subtle my-0 h-px w-full"></div>}
          <Button
            className="flex h-auto w-full cursor-pointer items-start gap-2 px-0 py-2 text-left transition-opacity hover:bg-transparent hover:opacity-70"
            onClick={() => onSelect?.(suggestion)}
            type="button"
            variant="ghost"
          >
            <span
              className="text-tertiary min-w-0 flex-1 font-['Inter'] text-[13px] leading-[170%] font-medium tracking-normal break-words whitespace-normal"
              style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
            >
              {suggestion.titleKey
                ? t(suggestion.titleKey, { defaultValue: suggestion.title })
                : suggestion.title}
            </span>
            <img
              alt=""
              className="icon-themed mt-0.5 h-[14px] w-[14px] flex-shrink-0 shrink-0"
              src={arrowIcon}
            />
          </Button>
        </React.Fragment>
      ))}
    </div>
  );
}

interface SuggestionCardsSkeletonProps {
  count?: number;
}

export function SuggestionCardsSkeleton({ count = 3 }: SuggestionCardsSkeletonProps) {
  return (
    <div className="flex w-full flex-col gap-0">
      {Array.from({ length: count }).map((_, index) => (
        <div className="flex w-full items-center justify-between px-0 py-2" key={index}>
          <Skeleton
            className="h-[14px] flex-1"
            style={{
              maxWidth: index === 0 ? '70%' : index === 1 ? '85%' : '60%',
            }}
          />
          <Skeleton className="h-[14px] w-[14px] shrink-0 rounded-sm" />
        </div>
      ))}
    </div>
  );
}
